/**
 * Download a PDF with a real "Save As" experience.
 *
 * Strategy (most iframe-safe → least):
 *   1. fetch() the URL as a blob.
 *   2. Call `showSaveFilePicker()` so the OS native Save dialog pops up
 *      and the user chooses the folder + filename. Works in Chromium ≥ 86
 *      over HTTPS, including inside iframes because it's a JS API, not a
 *      navigation / download trigger that iframe sandboxing blocks.
 *   3. Fall back to an anchor-click on the blob URL (Firefox/Safari path).
 *   4. As a last resort, navigate the top frame to the URL so the browser's
 *      own download manager handles it (bypasses iframe download blocks).
 *
 * @param {string} url  Fully-qualified backend PDF URL.
 * @param {string} filename  Suggested save filename (with .pdf).
 */
export async function downloadPdfWithSaveAs(url, filename) {
  // Detect iframe — Emergent preview iframes sandbox-block anchor
  // downloads silently (no exception, no download). Inside an iframe we
  // skip the blob/anchor path entirely and go straight to the new-tab
  // opener so the browser's Content-Disposition header takes over.
  // #168/#174 (Group H): window.open can return null when a popup
  // blocker / sandbox attribute (`sandbox="allow-scripts"` w/o
  // `allow-popups`) silently denies the new tab. Treat that as a fall-
  // through to the self-navigation path so the user still gets the file.
  const openNewTabSafely = (target) => {
    try {
      const w = window.open(target, "_blank", "noopener");
      if (w === null || typeof w === "undefined") {
        window.location.href = target;
        return "self-nav";
      }
      return "new-tab-nav";
    } catch {
      window.location.href = target;
      return "self-nav";
    }
  };

  const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
  if (inIframe) {
    return openNewTabSafely(url);
  }

  let blob;
  try {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    blob = await resp.blob();
  } catch (err) {
    return openNewTabSafely(url);
  }

  // (1) Native OS Save As dialog (Chromium ≥ 86, top-level only)
  if (typeof window.showSaveFilePicker === "function") {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: "PDF Document", accept: { "application/pdf": [".pdf"] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return "save-as";
    } catch (err) {
      if (err?.name === "AbortError") return "cancelled";
    }
  }

  // (2) Anchor click on a blob URL — Firefox/Safari path
  try {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try { document.body.removeChild(a); } catch (_) {}
      URL.revokeObjectURL(objectUrl);
    }, 4000);
    return "anchor";
  } catch (_) {
    return openNewTabSafely(url);
  }
}
